// pages/ProjectDetail.js
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaExternalLinkAlt, FaCheckCircle, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import projects, { getProjectBySlug } from '../../data/projects';
import PageHeader from '../../components/PageHeader';
import CtaBanner from '../../components/CtaBanner';
import NotFound from '../NotFound/NotFound';
import usePageTitle from '../../hooks/usePageTitle';
import '../Detail.css';

const ProjectDetail = () => {
    const { slug } = useParams();
    const project = getProjectBySlug(slug);

    usePageTitle(project ? project.title : 'Project Not Found', project ? project.description : undefined);

    if (!project) {
        return <NotFound />;
    }

    const index = projects.indexOf(project);
    const prevProject = projects[index - 1];
    const nextProject = projects[index + 1];
    const relatedProjects = projects
        .filter((item) => item.category === project.category && item.slug !== project.slug)
        .slice(0, 3);

    return (
        <>
            <PageHeader
                title={project.title}
                subtitle={project.description}
                breadcrumbs={[{ label: 'Portfolio', to: '/portfolio' }, { label: project.title }]}
            />

            <section className="section detail">
                <div className="container">
                    <div className="detail-showcase">
                        <div className="browser-frame">
                            <div className="browser-bar">
                                <span className="browser-dot red"></span>
                                <span className="browser-dot yellow"></span>
                                <span className="browser-dot green"></span>
                                {project.liveUrl && (
                                    <span className="browser-url">{project.liveUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
                                )}
                            </div>
                            <img src={project.image} alt={project.title} />
                        </div>
                    </div>

                    <div className="detail-layout">
                        <div className="detail-main">
                            <h2>Project Overview</h2>
                            <p>{project.overview}</p>

                            <h2>Key Features</h2>
                            <ul className="detail-list">
                                {project.features.map((feature) => (
                                    <li key={feature}>
                                        <FaCheckCircle />
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            {project.results && (
                                <>
                                    <h2>Highlights</h2>
                                    <div className="detail-highlights">
                                        {project.results.map((result) => (
                                            <div key={result} className="highlight-card">{result}</div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        <aside className="detail-sidebar">
                            <div className="card sidebar-card">
                                <h3>Project Info</h3>
                                <dl className="info-list">
                                    <div>
                                        <dt>Category</dt>
                                        <dd>{project.category}</dd>
                                    </div>
                                    {project.location && (
                                        <div>
                                            <dt>Location</dt>
                                            <dd>{project.location}</dd>
                                        </div>
                                    )}
                                    {project.year && (
                                        <div>
                                            <dt>Year</dt>
                                            <dd>{project.year}</dd>
                                        </div>
                                    )}
                                </dl>

                                <h4>Technologies</h4>
                                <div className="portfolio-tech">
                                    {project.technologies.map((tech) => (
                                        <span key={tech} className="tech-tag">{tech}</span>
                                    ))}
                                </div>

                                {project.liveUrl && (
                                    <a href={project.liveUrl} className="btn sidebar-btn" target="_blank" rel="noopener noreferrer">
                                        Visit Live Site <FaExternalLinkAlt />
                                    </a>
                                )}
                                <Link to="/contact" className="btn btn-outline sidebar-btn">
                                    Start a Similar Project
                                </Link>
                            </div>
                        </aside>
                    </div>

                    <div className="detail-pager">
                        {prevProject ? (
                            <Link to={`/portfolio/${prevProject.slug}`} className="pager-link">
                                <FaArrowLeft />
                                <span>
                                    <small>Previous</small>
                                    {prevProject.title}
                                </span>
                            </Link>
                        ) : <span />}
                        {nextProject && (
                            <Link to={`/portfolio/${nextProject.slug}`} className="pager-link next">
                                <span>
                                    <small>Next</small>
                                    {nextProject.title}
                                </span>
                                <FaArrowRight />
                            </Link>
                        )}
                    </div>

                    {relatedProjects.length > 0 && (
                        <div className="detail-related">
                            <h2>Related Projects</h2>
                            <div className="related-grid">
                                {relatedProjects.map((item) => (
                                    <Link key={item.slug} to={`/portfolio/${item.slug}`} className="related-card card">
                                        <img src={item.image} alt={item.title} loading="lazy" />
                                        <div className="related-content">
                                            <span className="related-category">{item.category}</span>
                                            <h4>{item.title}</h4>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <CtaBanner title="Want a project like this?" />
        </>
    );
};

export default ProjectDetail;
